import type {Params, RenderInitFields, ZuoraResponse} from '@github-ui/zuorajs'
import {Z} from '@github-ui/zuorajs'
import {
  createPaymentPageErrorHandler,
  type PaymentPageErrorHandler,
  type PaymentPageRefreshContext,
} from '@github-ui/zuorajs/payment-page-errors'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {observe} from '@github-ui/selector-observer'
import {reportError} from '@github-ui/failbot'
import {sendData, trackView} from '@github-ui/hydro-tracking'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

const paymentPageErrorHandlers = new WeakMap<HTMLElement, PaymentPageErrorHandler>()

async function setupZuoraHostedPaymentsPage(container: HTMLElement, cardSetupOverride?: boolean): Promise<boolean> {
  const cardFieldsPage = container.classList.contains('js-zuora-payment-page')
  const fallingBackFromCardSetup = cardSetupOverride === false && container.getAttribute('data-card-setup') === 'true'
  if (cardFieldsPage) prepareZuoraCardFieldsPage(container)
  let replacementRendered = false

  try {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const url = container.getAttribute('data-url')!
    const organizationId = container.getAttribute('data-organization-id')
    const businessId = container.getAttribute('data-business-id')
    const signatureViewContext = container.getAttribute('data-signature-view-context')
    const manualPayment = container.getAttribute('data-manual-payment')
    const cardSetup = cardSetupOverride ?? container.getAttribute('data-card-setup') === 'true'
    const termsOfService = container.getAttribute('data-terms-of-service')
    const target = container.getAttribute('data-target')
    const invoices = container.getAttribute('data-invoices')
    const paymentGateway = container.getAttribute('data-payment-gateway')
    const urlQuery = new URLSearchParams()
    if (organizationId) {
      urlQuery.append('organization_id', organizationId)
    }
    if (signatureViewContext) {
      urlQuery.append('view_context', signatureViewContext)
    }
    if (businessId) {
      urlQuery.append('business_id', businessId)
    }
    if (manualPayment) {
      urlQuery.append('manual_payment', manualPayment)
    }
    if (cardSetup) {
      urlQuery.append('card_setup', 'true')
    }
    if (termsOfService) {
      urlQuery.append('terms_of_service', termsOfService)
    }
    if (target) {
      urlQuery.append('target', target)
    }
    if (invoices) {
      for (const invoice of invoices.split(',')) {
        urlQuery.append('invoices[]', invoice)
      }
    }
    if (paymentGateway) {
      urlQuery.append('payment_gateway', paymentGateway)
    }

    const response = await fetch(`${url}?${urlQuery.toString()}`, {
      headers: {
        ...getBaseFetchHeaders(),
        Accept: 'application/json',
      },
    })
    if (!response.ok) {
      const responseError = new Error()
      const statusText = response.statusText ? ` ${response.statusText}` : ''
      responseError.message = `HTTP ${response.status}${statusText}`
      throw responseError
    }
    const params: Params = await response.json()

    if (params['error']) {
      replaceSpinnerWithMessage(params['error'])
      announce(params['error'], {assertive: true})
      return false
    }

    const renderPromise = waitForZuoraRender()
    if (cardFieldsPage) {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const userId = container.getAttribute('data-id')!
      loadZuora(
        container,
        params,
        {creditCardHolderName: userId},
        zuoraSuccessCallback(container, cardSetup, paymentMethodPageCallback),
      )
    } else {
      loadZuora(
        container,
        params,
        {creditCardHolderName: 'GitHub Organization'},
        zuoraSuccessCallback(container, cardSetup),
      )
    }

    await renderPromise
    replacementRendered = true
    if (cardFieldsPage) finishZuoraCardFieldsPage(container)
    updateCardSetupPaymentMessages(cardSetup)
    if (fallingBackFromCardSetup) {
      const oneTimePaymentMessage = document.querySelector<HTMLElement>('.js-one-time-payment-message:not([hidden])')
      const announcement = oneTimePaymentMessage?.textContent?.trim()
      if (announcement) announce(announcement)
    }
    return true
  } catch (error) {
    const errorMessage = container.getAttribute('data-general-signature-error-message') || ''
    replaceSpinnerWithMessage(errorMessage)
    throw error
  } finally {
    if (cardFieldsPage && !replacementRendered) restoreZuoraCardFieldsPage(container)
  }
}

async function setupInitialZuoraHostedPaymentsPage(container: HTMLElement): Promise<void> {
  try {
    await setupZuoraHostedPaymentsPage(container)
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)))
  }
}

type PaymentMethodDetails = {
  masked_number: string
  card_type: string
}

async function fetchPaymentMethodDetails(
  zuoraResponse: ZuoraResponse,
  orgId: string | null,
  callback: (response: PaymentMethodDetails) => void,
) {
  const params = new URLSearchParams()
  params.append('payment_method_id', zuoraResponse.refId)
  if (orgId) {
    params.append('organization_id', orgId)
  }

  const response = await fetch(`/settings/billing/payment_method?${params.toString()}`, {
    headers: {
      ...getBaseFetchHeaders(),
      Accept: 'application/json',
    },
  })
  if (!response.ok) {
    const responseError = new Error()
    const statusText = response.statusText ? ` ${response.statusText}` : ''
    responseError.message = `HTTP ${response.status}${statusText}`
    throw responseError
  }

  callback(await response.json())
}

function paymentPageErrorHandler(container: HTMLElement): PaymentPageErrorHandler {
  const currentHandler = paymentPageErrorHandlers.get(container)
  if (currentHandler) return currentHandler

  const handler = createPaymentPageErrorHandler({
    container,
    allowCardSetupFallback:
      container.hasAttribute('data-card-setup-fallback-supported') ||
      (container.getAttribute('data-manual-payment') === 'true' && !container.hasAttribute('data-post-success-form')),
    refreshPaymentPage: cardSetup => setupZuoraHostedPaymentsPage(container, cardSetup),
    reportError,
    sendErrorMessage: (key, message) => Z.sendErrorMessageToHpm(key, message),
    trackError: context => {
      container.setAttribute('data-hydro-client-context', JSON.stringify(context))
      trackView(container)
    },
    trackRefresh: context => trackPaymentPageRefresh(container, context),
  })
  paymentPageErrorHandlers.set(container, handler)
  return handler
}

function trackPaymentPageRefresh(container: HTMLElement, context: PaymentPageRefreshContext) {
  const payload = container.getAttribute('data-hpp-recovery-payload')
  const hmac = container.getAttribute('data-hpp-recovery-hmac')
  if (!payload || !hmac) return

  sendData(payload, hmac, JSON.stringify(context))
}

function errorMessageCallback(container: HTMLElement) {
  return async (key: string, code: string, message: string) => {
    await paymentPageErrorHandler(container)({key, code, message, source: 'error_handler'})
  }
}

type ZuoraCallback = (response: ZuoraResponse) => void
type AsyncZuoraCallback = (response: ZuoraResponse) => Promise<void>

async function pollPayment(
  paymentId: string,
  organizationId?: string | null,
  cardSetup?: boolean | null,
  businessId?: string | null,
  maxRetries = 5,
  timeBetweenRequests = 1000,
): Promise<Response> {
  if (maxRetries === 0) {
    throw new Error(`Max retries exhausted`)
  }

  let checkUrl: string
  if (businessId) {
    const basePath = cardSetup
      ? `/enterprises/${businessId}/settings/billing/card_setup`
      : `/enterprises/${businessId}/billing/bill_pay`
    checkUrl = `${basePath}/${paymentId}`
  } else {
    const basePath = cardSetup ? '/settings/billing/card_setup' : '/settings/billing/bill_pay'
    checkUrl = `${basePath}/${paymentId}`
    if (organizationId) {
      const urlQuery = new URLSearchParams()
      urlQuery.append('organization_id', organizationId)
      checkUrl = `${checkUrl}?${urlQuery.toString()}`
    }
  }

  const response = await fetch(checkUrl, {
    headers: {
      ...getBaseFetchHeaders(),
    },
  })
  if (response.status === 200) {
    return response
  }
  if (response.status === 202) {
    await new Promise(resolve => setTimeout(resolve, timeBetweenRequests))
    return pollPayment(paymentId, organizationId, cardSetup, businessId, maxRetries - 1, timeBetweenRequests * 1.5)
  }

  throw new Error(`Unexpected ${response.status} response from poll endpoint`)
}

function replaceLocation(url: string | null): boolean {
  if (!url) {
    return false
  }

  try {
    const validatedUrl = new URL(url, window.location.origin)
    if (validatedUrl.protocol === 'http:' || validatedUrl.protocol === 'https:') {
      window.location.replace(validatedUrl.toString())
      return true
    }
  } catch {
    return false
  }

  return false
}

// Submits the form referenced by the given selector attribute on the container, if present.
// Returns true when a form was submitted.
function submitForm(container: HTMLElement, attribute: string): boolean {
  const selector = container.getAttribute(attribute)
  if (!selector) return false

  const form = document.querySelector(selector)
  if (!(form instanceof HTMLFormElement)) return false

  form.submit()
  return true
}

// These forms finalize advance payments and validate payment server-side, without waiting for
// the webhook's billing transaction. Returns true when a form was submitted.
function submitPostSuccessForm(container: HTMLElement): boolean {
  return submitForm(container, 'data-post-success-form')
}

function zuoraSuccessCallback(
  container: HTMLElement,
  cardSetup: boolean,
  callback?: ZuoraCallback | null,
): AsyncZuoraCallback {
  return async response => {
    if (callback) callback(response)

    if (response.success !== 'true' && response.success !== true) {
      await paymentPageErrorHandler(container)({
        key: 'error',
        code: response.errorCode,
        message: response.errorMessage,
        source: 'callback',
      })
      return
    }

    if (response.success === 'true' || response.success === true) {
      const form = document.querySelector('.js-payment-method-form')
      if (form) {
        if (!(form instanceof HTMLFormElement)) return
        // We are in a page with only the credit card form that can be submitted
        const redirectTo = container.getAttribute('data-redirect-to')
        if (redirectTo) {
          // redirectTo is only added when we are in the manual payment page.
          if (response.AuthorizeResult === 'Approved') {
            const orgId = container.getAttribute('data-organization-id')
            const bizId = container.getAttribute('data-business-id')
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            const spinner = document.querySelector<HTMLElement>('.js-payment-verification-spinner')!
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            const success = document.querySelector<HTMLElement>('.js-payment-verification-success')!
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            const paymentWrapper = document.querySelector<HTMLElement>('.js-payment-method-section-wrapper')!
            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            const stillProcessing = document.querySelector<HTMLElement>('.js-payment-still-processing')!

            try {
              paymentWrapper.hidden = true
              spinner.hidden = false

              if (submitPostSuccessForm(container)) return

              await pollPayment(response.PaymentId, orgId, cardSetup, bizId)

              spinner.hidden = true
              success.hidden = false
            } catch {
              spinner.hidden = true
              stillProcessing.hidden = false

              // Give the customer a moment to read the "still processing" notice.
              await new Promise(resolve => setTimeout(resolve, 3000))
            }

            await new Promise(resolve => setTimeout(resolve, 150))
            replaceLocation(redirectTo)
          } else {
            // Unlikely we'll hit this point but because Zuora doesn't have good documentation
            // Sending an error to Sentry is the best way to get awareness that there's other cases
            reportError(new Error(`Unknown AuthorizationResult ${response.AuthorizeResult}`))
            replaceLocation(redirectTo)
          }
        } else {
          form.submit()
        }
      } else {
        const orgId = container.getAttribute('data-organization-id')
        fetchPaymentMethodDetails(response, orgId, (res: PaymentMethodDetails) => {
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          const infoContainer = document.querySelector<HTMLElement>('.js-zuora-billing-info')!
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          const newCardTypeContainer = infoContainer.querySelector<HTMLElement>('.js-new-card-type')!
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          const newCardNumberContainer = infoContainer.querySelector<HTMLElement>('.js-new-card-number')!
          const newCardLastFour = res.masked_number.replace(/\*/g, '')
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          const paymentMethodsContainer = container.closest<HTMLElement>('.js-billing-section')!
          const awaitingPayment = document.querySelector('.js-awaiting-payment')

          newCardTypeContainer.textContent = res.card_type
          newCardNumberContainer.textContent = newCardLastFour
          /* eslint-disable-next-line github/no-d-none */
          container.classList.add('d-none')
          /* eslint-disable-next-line github/no-d-none */
          infoContainer.classList.remove('d-none')

          if (awaitingPayment) {
            awaitingPayment.toggleAttribute('hidden')
          }

          paymentMethodsContainer.classList.remove('PaymentMethod--creditcard')
          paymentMethodsContainer.classList.add('PaymentMethod--creditcard-added')
        })
      }
    }
  }
}

function loadZuora(container: HTMLElement, params: Params, initFields: RenderInitFields, callback: ZuoraCallback) {
  const initFieldsWithPopulatedFields = Object.assign({}, params.prepopulate, initFields)
  delete params.prepopulate

  Z.renderWithErrorHandler(params, initFieldsWithPopulatedFields, callback, errorMessageCallback(container))
}

function paymentMethodPageCallback(response: ZuoraResponse) {
  if (response.success === 'true' || response.success === true) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const infoContainer = document.querySelector<HTMLElement>('.js-zuora-billing-info')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const paymentMethodIDEl = infoContainer.querySelector<HTMLInputElement>('.js-zuora-payment-method-id')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const countryCodeEl = infoContainer.querySelector<HTMLInputElement>('.js-zuora-select-country')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const regionEl = infoContainer.querySelector<HTMLInputElement>('.js-zuora-select-region')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const postalCodeEl = infoContainer.querySelector<HTMLInputElement>('.js-zuora-select-postal-code')!

    paymentMethodIDEl.value = response.refId
    countryCodeEl.value = countryCodeEl.value || response.creditCardCountry || ''
    regionEl.value = regionEl.value || response.creditCardState || ''
    postalCodeEl.value = postalCodeEl.value || response.creditCardPostalCode || ''
  }
}

function resizeZuoraIframe() {
  const iframe = document.getElementById('z_hppm_iframe')

  if (iframe != null) {
    if (window.innerWidth < 544) {
      iframe.style.width = '100%'
      iframe.style.height = '400px'
    } else {
      iframe.style.width = '95%'
      iframe.style.height = '300px'
    }
  }
}

function replaceSpinnerWithMessage(message: string) {
  const spinner = document.querySelector('.js-zuora-spinner')
  if (spinner) spinner.textContent = message
}

function updateCardSetupPaymentMessages(cardSetup: boolean) {
  for (const message of document.querySelectorAll<HTMLElement>('.js-card-setup-payment-message')) {
    message.hidden = !cardSetup
  }
  for (const message of document.querySelectorAll<HTMLElement>('.js-one-time-payment-message')) {
    message.hidden = cardSetup
  }
}

function prepareZuoraCardFieldsPage(container: HTMLElement) {
  const spinner = document.querySelector('.js-zuora-spinner')
  /* eslint-disable-next-line github/no-d-none */
  if (spinner) spinner.classList.remove('d-none')
  container.style.opacity = '0'
}

function finishZuoraCardFieldsPage(container: HTMLElement) {
  const spinner = document.querySelector('.js-zuora-spinner')
  /* eslint-disable-next-line github/no-d-none */
  if (spinner) spinner.classList.add('d-none')
  container.style.opacity = '1'
  resizeZuoraIframe()
}

function restoreZuoraCardFieldsPage(container: HTMLElement) {
  container.style.opacity = '1'
}

function waitForZuoraRender(): Promise<void> {
  return new Promise(resolve => {
    Z.runAfterRender(resolve)
  })
}

observe('#z_hppm_iframe', {
  constructor: HTMLIFrameElement,
  add(iframe) {
    // Override Zuora's default #FAFAFA background so the parent theme shows through
    iframe.style.backgroundColor = 'transparent'

    const container = document.getElementById('zuora_payment')
    const title = container?.getAttribute('title')
    if (title) {
      iframe.setAttribute('title', title)
    }
  },
})

observe('#zuora_payment', {
  constructor: HTMLElement,
  add(el) {
    const cardFieldsPage = el.classList.contains('js-zuora-payment-page')
    if (cardFieldsPage) {
      /* eslint-disable-next-line github/prefer-observers */
      window.addEventListener('resize', debounce(resizeZuoraIframe, 200))
    }

    void setupInitialZuoraHostedPaymentsPage(el)
  },
})
